import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, message, Tabs } from 'antd';
import { createDomain, updateDomain } from '../../Utils/domains/domains';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Info } from 'lucide-react';

const { TabPane } = Tabs;

const DomainCreationPopup = ({ visible, onCancel, clientId, onSubmit, initialValues, isEditing }) => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const location = useLocation();
  const userInfo = useSelector(state => state.userInfo);
  const [activeTab, setActiveTab] = useState('1');

  useEffect(() => {
    if (isEditing && initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [isEditing, initialValues, form]);

  const handleSubmit = async () => {
    try {
      let values;
      if (isEditing) {
        if (activeTab === '2') {
          // change Newpassword to password in the form
          passwordForm.setFieldsValue({ password: passwordForm.getFieldValue('new_password') });
          const passwordData = await passwordForm.validateFields();
          values = { 
            password: passwordData.new_password,
            email: passwordData.email
          };
        } else {
          values = await form.validateFields();
        }
      } else {
        values = await form.validateFields();
        // change Newpassword to password in the form and domain_manager_email to email
        values = {
          ...values,
          password: values.Newpassword,
          email: values.domain_manager_email
        };
      }

      values.user_id = clientId;
      values.company_name = userInfo?.company_name;
      
      if (values.domain_manager_name) {
        const nameParts = values.domain_manager_name?.split(' ');
        values.domain_manager_first_name = nameParts[0];
        values.domain_manager_last_name = nameParts.slice(1).join(' ');
      }

      if (isEditing) {
        await updateDomain(initialValues.domain_id, values);
        message.success('Domaine mis à jour avec succès!');
      } else {
        await createDomain(values);
        message.success('Domaine créé avec succès!');
      }

      location.state = { refresh: true };
      onSubmit(values);
      onCancel();
    } catch (error) {
      console.error(error);
      message.error(isEditing ? 'Erreur lors de la mise à jour du domaine' : 'Erreur lors de la création du domaine');
    }
  };

  const renderDomainInfoForm = () => (
    <Form form={form} layout="vertical" name="domain_form">
      <Form.Item
        name="domain_name"
        label="Nom du Domaine"
        rules={[{ required: true, message: 'Veuillez saisir le nom du domaine !' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="domain_manager_name"
        label="Nom et Prénom du Gestionnaire"
      >
        <Input />
      </Form.Item>
      {
        !isEditing && 
        <Form.Item
          name="domain_manager_email"
          label="Email du Gestionnaire"
          rules={[
            { type: 'email', message: 'Veuillez entrer un email valide !' },
            { required: true, message: 'Veuillez saisir l\'email du gestionnaire !' }
          ]}
        >
          <Input />
        </Form.Item>
      }
      <Form.Item
        name="phone_number"
        label="Numéro de Téléphone"
      >
        <Input />
      </Form.Item>
      {!isEditing && (
        <>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'Veuillez entrer un email valide !' }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="Newpassword"
            label="Mot de passe"
            rules={[
              { required: true, message: 'Veuillez saisir le mot de passe !' },
              { min: 8, message: 'Le mot de passe doit contenir au moins 8 caractères !' }
            ]}
          >
            <Input.Password autoComplete="new-password"  />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="Confirmer le mot de passe"
            dependencies={['Newpassword']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value && !getFieldValue('Newpassword')) { // If both fields are empty return success
                    return Promise.resolve();
                  }
                  if (!value && getFieldValue('Newpassword')) { // If only confirm password is empty return error
                    return Promise.reject(new Error('Veuillez confirmer votre mot de passe !'));
                  }
                  if (getFieldValue('Newpassword') === value) { // If both fields match return success
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Les deux mots de passe ne correspondent pas !')); // If both fields don't match return error
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password"  />
          </Form.Item>
        </>
      )}
    </Form>
  );

  const renderPasswordChangeForm = () => (
    <>
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-md border border-blue-200 mb-4">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="space-y-2">
          <p className="text-blue-700 text-sm">
            Si vous entrez une adresse email déjà associée à un compte existant, 
            ce compte sera automatiquement assigné comme gestionnaire de ce domaine.
          </p>
          <p className="text-blue-700 text-sm">
            Si l'email n'est pas associé à un compte existant, vous devrez définir un mot de passe 
            pour créer un nouveau compte.
          </p>
          <p className='text-red-500 text-sm'>
            <strong>Important :</strong> Pour modifier l'adresse e-mail du gestionnaire, cliquez sur son profil, puis modifiez directement l'adresse e-mail.  
          </p>
        </div>
      </div>
      <Form form={passwordForm} layout="vertical" name="password_change_form">
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { type: 'email', message: 'Veuillez entrer un email valide !' }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="new_password"
          label="Nouveau mot de passe"
          rules={[
            { min: 8, message: 'Le mot de passe doit contenir au moins 8 caractères !' }
          ]}
        >
          <Input.Password autoComplete="new-password"  />
        </Form.Item>
        <Form.Item
          name="confirm_new_password"
          label="Confirmer le nouveau mot de passe"
          dependencies={['new_password']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value && !getFieldValue('new_password')) {
                  return Promise.resolve();
                }
                if (!value && getFieldValue('new_password')) {
                  return Promise.reject(new Error('Veuillez confirmer votre mot de passe !'));
                }
                if (getFieldValue('new_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Les deux mots de passe ne correspondent pas !'));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password"  />
        </Form.Item>
      </Form>
    </>
  );

  return (
    <Modal
      open={visible}
      title={isEditing ? "Modifier le Domaine" : "Créer un Domaine"}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <Button key="submit" className="bg-indigo-500 text-white hover:bg-indigo-600" onClick={handleSubmit}>
          {isEditing ? "Modifier" : "Créer"}
        </Button>
      ]}
    >
      {isEditing ? (
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Informations du domaine" key="1">
            {renderDomainInfoForm()}
          </TabPane>
          <TabPane tab="Email et Mot de passe" key="2">
            {renderPasswordChangeForm()}
          </TabPane>
        </Tabs>
      ) : (
        renderDomainInfoForm()
      )}
    </Modal>
  );
};

export default DomainCreationPopup;